/**
 * Correspondance des colonnes d'un fichier d'écritures (EF-0411).
 *
 * Le serveur fait foi pour la lecture et les contrôles ; ici se tient ce qui
 * rend l'écran commode : proposer une correspondance depuis les en-têtes du
 * fichier, et dire, avant tout envoi, ce qui manque pour qu'elle tienne.
 */

export type ChampImporte =
  | "JOURNAL"
  | "DATE"
  | "PIECE"
  | "LIBELLE"
  | "REFERENCE"
  | "COMPTE"
  | "TIERS"
  | "DEBIT"
  | "CREDIT"
  | "MONTANT"
  | "SENS"
  | "ECHEANCE"
  | "CODE_TVA";

/** Champ porté par chaque colonne, par rang à partir de zéro. */
export type Affectation = Record<number, ChampImporte | undefined>;

const replier = (texte: string) =>
  texte
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

/**
 * Mots qui, dans un en-tête, désignent chaque champ. L'ordre compte : « date
 * d'échéance » est une échéance avant d'être une date, « n° de compte » un
 * compte et non une pièce.
 */
const INDICES: [ChampImporte, RegExp][] = [
  ["ECHEANCE", /\b(echeance|echeances|due)\b/],
  ["CODE_TVA", /\b(code tva|taux tva|tva code|code taxe)\b/],
  ["DEBIT", /\b(debit|debits)\b/],
  ["CREDIT", /\b(credit|credits)\b/],
  ["MONTANT", /\b(montant|amount|somme)\b/],
  ["SENS", /\b(sens|d c|dc)\b/],
  ["COMPTE", /\b(compte|comptes|cpt|account|n compte|numero de compte|general)\b/],
  ["TIERS", /\b(tiers|auxiliaire|aux|fournisseur|client)\b/],
  ["JOURNAL", /\b(journal|jnl|jal|code journal)\b/],
  ["DATE", /\b(date|jour)\b/],
  ["PIECE", /\b(piece|n piece|numero piece|ecriture|mouvement)\b/],
  ["REFERENCE", /\b(reference|ref|facture|document)\b/],
  ["LIBELLE", /\b(libelle|intitule|designation|description|label)\b/],
];

/** Propose une affectation depuis les en-têtes : chaque champ à la première colonne qui le nomme. */
export function proposerAffectation(entetes: string[]): Affectation {
  const affectation: Affectation = {};
  const pris = new Set<ChampImporte>();
  entetes.forEach((entete, rang) => {
    const repli = replier(entete);
    if (repli === "") return;
    const trouve = INDICES.find(([champ, motif]) => !pris.has(champ) && motif.test(repli));
    if (trouve != null) {
      affectation[rang] = trouve[0];
      pris.add(trouve[0]);
    }
  });
  return affectation;
}

/** Colonnes par champ, telles que l'interface les attend. */
export function colonnesDe(affectation: Affectation): Partial<Record<ChampImporte, number>> {
  const colonnes: Partial<Record<ChampImporte, number>> = {};
  for (const [rang, champ] of Object.entries(affectation)) {
    if (champ != null) colonnes[champ] = Number(rang);
  }
  return colonnes;
}

/** Affectation par colonne, depuis des colonnes par champ. */
export function affectationDe(colonnes: Partial<Record<ChampImporte, number>>): Affectation {
  const affectation: Affectation = {};
  for (const [champ, rang] of Object.entries(colonnes)) {
    if (rang != null) affectation[rang] = champ as ChampImporte;
  }
  return affectation;
}

/**
 * Ce qui manque pour que la correspondance tienne — les mêmes règles que le
 * serveur, dites avant l'envoi. Une liste vide : on peut contrôler.
 */
export function manques(affectation: Affectation, journalParDefaut: string): string[] {
  const champs = new Set(Object.values(affectation).filter((c): c is ChampImporte => c != null));
  const liste: string[] = [];
  if (!champs.has("DATE")) liste.push("la colonne de date");
  if (!champs.has("COMPTE")) liste.push("la colonne de compte");
  if (!champs.has("JOURNAL") && journalParDefaut.trim() === "") liste.push("la colonne de journal, ou un journal par défaut");
  const debitCredit = champs.has("DEBIT") || champs.has("CREDIT");
  if (!debitCredit && !champs.has("MONTANT")) liste.push("les montants : débit et crédit, ou un montant");
  if (debitCredit && champs.has("MONTANT")) liste.push("un seul mode de montant : débit et crédit, ou un montant");
  if (champs.has("SENS") && !champs.has("MONTANT")) liste.push("une colonne de montant pour accompagner le sens");
  return liste;
}

/** Nom de colonne à la façon d'un tableur : A, B… Z, AA. */
export function lettreDeColonne(rang: number): string {
  let lettres = "";
  let n = rang + 1;
  while (n > 0) {
    const reste = (n - 1) % 26;
    lettres = String.fromCharCode(65 + reste) + lettres;
    n = Math.floor((n - 1) / 26);
  }
  return lettres;
}
